export type ConsultationStatus = "open" | "closed";

/** Constantes vitales relevées lors de la consultation. */
export interface ConsultationVitals {
  bloodPressureSystolic: number | null;
  bloodPressureDiastolic: number | null;
  heartRate: number | null;
  temperatureC: number | null;
  respiratoryRate: number | null;
  oxygenSaturation: number | null;
  weightKg: number | null;
  heightCm: number | null;
  painScore: number | null;
  notes: string;
  recordedAt: string | null;
}

export type ConsultationVitalsInput = Partial<
  Omit<ConsultationVitals, "notes" | "recordedAt">
> & {
  notes?: string;
  recordedAt?: string | null;
};

export interface Consultation {
  id: string;
  patientId: string;
  motif: string;
  diagnostic: string;
  acts: string;
  vitals: ConsultationVitals | null;
  status: ConsultationStatus;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
}

export interface ConsultationInput {
  patientId: string;
  motif: string;
  diagnostic?: string;
  acts?: string;
  vitals?: ConsultationVitalsInput | null;
}

export type PrescriptionStatus =
  | "draft"
  | "sent_to_pharmacy"
  | "pharmacist_validated"
  | "validated_for_billing"
  | "dispensed";

/** Origine de l'ordonnance — clinique (médecin) ou saisie directe officine. */
export type PrescriptionSource = "clinical" | "pharmacy_direct";

export interface PrescriptionLine {
  id: string;
  productId: string;
  productLabel: string;
  quantity: number;
  /** Fréquence de prise (vide = données anciennes). */
  frequency: PrescriptionFrequency | "";
  /** Durée du traitement en jours. */
  durationDays: number | null;
  /** Moments de prise (matin, soir…). */
  timing: PrescriptionTiming[];
  /** Notes libres / posologie détaillée. */
  instructions: string;
  /** Suivi dispensation (H3). */
  fulfillment: PrescriptionLineFulfillment;
}

export type PrescriptionLineStockState =
  | "unchecked"
  | "reserved"
  | "shortage"
  | "issued";

export type PrescriptionLineDispenseState =
  | "pending"
  | "ready"
  | "delivered"
  | "external";

export interface PrescriptionLineFulfillment {
  stockState: PrescriptionLineStockState;
  dispenseState: PrescriptionLineDispenseState;
  billingDocumentId: string | null;
}

export function emptyPrescriptionLineFulfillment(): PrescriptionLineFulfillment {
  return {
    stockState: "unchecked",
    dispenseState: "pending",
    billingDocumentId: null,
  };
}

export const PRESCRIPTION_LINE_STOCK_LABELS: Record<PrescriptionLineStockState, string> = {
  unchecked: "Non vérifié",
  reserved: "Réservé",
  shortage: "Rupture",
  issued: "Sorti du stock",
};

export const PRESCRIPTION_LINE_DISPENSE_LABELS: Record<PrescriptionLineDispenseState, string> = {
  pending: "En attente",
  ready: "Prêt à livrer",
  delivered: "Livré",
  external: "À trouver ailleurs",
};

export type PrescriptionFrequency =
  | "once_daily"
  | "twice_daily"
  | "thrice_daily"
  | "four_times_daily"
  | "every_8h"
  | "every_12h"
  | "as_needed"
  | "once";

export type PrescriptionTiming =
  | "morning"
  | "noon"
  | "evening"
  | "bedtime"
  | "before_meal"
  | "after_meal"
  | "fasting";

export interface Prescription {
  id: string;
  consultationId: string;
  patientId: string;
  lines: PrescriptionLine[];
  status: PrescriptionStatus;
  /** Absent sur les données anciennes = ordonnance clinique. */
  source?: PrescriptionSource;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
}

export interface PrescriptionLineInput {
  productId: string;
  productLabel: string;
  quantity: number;
  frequency?: PrescriptionFrequency | "";
  durationDays?: number | null;
  timing?: PrescriptionTiming[];
  instructions?: string;
}

export const PRESCRIPTION_FREQUENCY_OPTIONS: PrescriptionFrequency[] = [
  "once_daily",
  "twice_daily",
  "thrice_daily",
  "four_times_daily",
  "every_8h",
  "every_12h",
  "as_needed",
  "once",
];

export const PRESCRIPTION_FREQUENCY_LABELS: Record<PrescriptionFrequency, string> = {
  once_daily: "1 fois / jour",
  twice_daily: "2 fois / jour",
  thrice_daily: "3 fois / jour",
  four_times_daily: "4 fois / jour",
  every_8h: "Toutes les 8 h",
  every_12h: "Toutes les 12 h",
  as_needed: "Si besoin",
  once: "Prise unique",
};

export const PRESCRIPTION_TIMING_OPTIONS: PrescriptionTiming[] = [
  "morning",
  "noon",
  "evening",
  "bedtime",
  "before_meal",
  "after_meal",
  "fasting",
];

export const PRESCRIPTION_TIMING_LABELS: Record<PrescriptionTiming, string> = {
  morning: "Matin",
  noon: "Midi",
  evening: "Soir",
  bedtime: "Coucher",
  before_meal: "Avant repas",
  after_meal: "Après repas",
  fasting: "À jeun",
};

export const DEFAULT_PRESCRIPTION_POSOLOGY = {
  frequency: "twice_daily" as PrescriptionFrequency,
  durationDays: 7,
  timing: ["morning", "evening"] as PrescriptionTiming[],
};

export function normalizePrescriptionTiming(raw: unknown): PrescriptionTiming[] {
  if (!Array.isArray(raw)) return [];
  const valid = new Set(PRESCRIPTION_TIMING_OPTIONS);
  return raw.filter((t): t is PrescriptionTiming => typeof t === "string" && valid.has(t as PrescriptionTiming));
}

export function formatPrescriptionPosology(line: Pick<
  PrescriptionLine,
  "frequency" | "durationDays" | "timing" | "instructions"
>): string {
  const parts: string[] = [];
  if (line.frequency) {
    parts.push(PRESCRIPTION_FREQUENCY_LABELS[line.frequency]);
  }
  if (line.timing.length > 0) {
    parts.push(line.timing.map((t) => PRESCRIPTION_TIMING_LABELS[t]).join(", "));
  }
  if (line.durationDays != null && line.durationDays > 0) {
    parts.push(`${line.durationDays} j`);
  }
  if (line.instructions.trim()) {
    parts.push(line.instructions.trim());
  }
  if (parts.length === 0) return "—";
  return parts.join(" · ");
}

export const CONSULTATION_STATUS_LABELS: Record<ConsultationStatus, string> = {
  open: "En cours",
  closed: "Clôturée",
};

export const PRESCRIPTION_STATUS_LABELS: Record<PrescriptionStatus, string> = {
  draft: "Brouillon",
  sent_to_pharmacy: "En attente pharmacien",
  pharmacist_validated: "Stock validé",
  validated_for_billing: "En comptabilité",
  dispensed: "Dispensée",
};

export const PRESCRIPTION_SOURCE_LABELS: Record<PrescriptionSource, string> = {
  clinical: "Médecin",
  pharmacy_direct: "Officine directe",
};

export function emptyConsultationVitals(): ConsultationVitals {
  return {
    bloodPressureSystolic: null,
    bloodPressureDiastolic: null,
    heartRate: null,
    temperatureC: null,
    respiratoryRate: null,
    oxygenSaturation: null,
    weightKg: null,
    heightCm: null,
    painScore: null,
    notes: "",
    recordedAt: null,
  };
}

function vitalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function normalizeConsultationVitals(raw: unknown): ConsultationVitals | null {
  if (!raw || typeof raw !== "object") return null;
  const v = raw as Record<string, unknown>;
  const vitals: ConsultationVitals = {
    bloodPressureSystolic: vitalNumber(v.bloodPressureSystolic),
    bloodPressureDiastolic: vitalNumber(v.bloodPressureDiastolic),
    heartRate: vitalNumber(v.heartRate),
    temperatureC: vitalNumber(v.temperatureC),
    respiratoryRate: vitalNumber(v.respiratoryRate),
    oxygenSaturation: vitalNumber(v.oxygenSaturation),
    weightKg: vitalNumber(v.weightKg),
    heightCm: vitalNumber(v.heightCm),
    painScore: vitalNumber(v.painScore),
    notes: String(v.notes ?? "").trim(),
    recordedAt: v.recordedAt ? String(v.recordedAt) : null,
  };
  return hasAnyVitalValue(vitals) ? vitals : null;
}

export function hasAnyVitalValue(vitals: ConsultationVitals | null | undefined): boolean {
  if (!vitals) return false;
  return (
    vitals.bloodPressureSystolic != null ||
    vitals.bloodPressureDiastolic != null ||
    vitals.heartRate != null ||
    vitals.temperatureC != null ||
    vitals.respiratoryRate != null ||
    vitals.oxygenSaturation != null ||
    vitals.weightKg != null ||
    vitals.heightCm != null ||
    vitals.painScore != null ||
    vitals.notes.length > 0
  );
}

export function buildConsultationVitals(input: ConsultationVitalsInput): ConsultationVitals | null {
  const vitals: ConsultationVitals = {
    bloodPressureSystolic: vitalNumber(input.bloodPressureSystolic),
    bloodPressureDiastolic: vitalNumber(input.bloodPressureDiastolic),
    heartRate: vitalNumber(input.heartRate),
    temperatureC: vitalNumber(input.temperatureC),
    respiratoryRate: vitalNumber(input.respiratoryRate),
    oxygenSaturation: vitalNumber(input.oxygenSaturation),
    weightKg: vitalNumber(input.weightKg),
    heightCm: vitalNumber(input.heightCm),
    painScore: vitalNumber(input.painScore),
    notes: input.notes?.trim() ?? "",
    recordedAt: input.recordedAt ?? null,
  };
  if (!hasAnyVitalValue(vitals)) return null;
  if (!vitals.recordedAt) vitals.recordedAt = new Date().toISOString();
  return vitals;
}

export function formatBloodPressure(vitals: ConsultationVitals | null | undefined): string | null {
  if (!vitals) return null;
  const { bloodPressureSystolic: sys, bloodPressureDiastolic: dia } = vitals;
  if (sys != null && dia != null) return `${sys}/${dia} mmHg`;
  if (sys != null) return `${sys} mmHg (sys)`;
  if (dia != null) return `${dia} mmHg (dia)`;
  return null;
}

export function formatVitalsSummary(vitals: ConsultationVitals | null | undefined): string {
  if (!vitals || !hasAnyVitalValue(vitals)) return "";
  const parts: string[] = [];
  const bp = formatBloodPressure(vitals);
  if (bp) parts.push(`TA ${bp.replace(" mmHg", "")}`);
  if (vitals.heartRate != null) parts.push(`Pouls ${vitals.heartRate} bpm`);
  if (vitals.temperatureC != null) parts.push(`${vitals.temperatureC} °C`);
  if (vitals.respiratoryRate != null) parts.push(`FR ${vitals.respiratoryRate}/min`);
  if (vitals.oxygenSaturation != null) parts.push(`SpO₂ ${vitals.oxygenSaturation}%`);
  if (vitals.weightKg != null) parts.push(`${vitals.weightKg} kg`);
  if (vitals.painScore != null) parts.push(`EVA ${vitals.painScore}/10`);
  return parts.join(" · ");
}
